import type {
  InstrumentSetupResponse,
  InstrumentSetupSummary,
} from "../types/instrumentSetup";
import type { UseActiveBrokerAccountResult } from "../contexts/ActiveBrokerAccountContext";
import { fetchInstrumentSetup } from "../services/brokerIntegrationApi";
import { accountNeedsInstrumentSetup } from "./instrumentMarketDataReadiness";

export type InstrumentSetupAfterSyncInput = {
  brokerAccountCode: string;
  instrumentSetup?: InstrumentSetupSummary | null;
  /** From GET /broker/trade-accounts `instrument_setup_pending` when already known. */
  instrumentSetupPendingOnAccount?: boolean | null;
  refetchTradeAccounts: UseActiveBrokerAccountResult["refetch"];
  /** When true, skip the accounts refetch (caller already refreshed). */
  skipTradeAccountsRefetch?: boolean;
  openMandatorySetup: (
    accountCode: string,
    options?: { expectedPending?: boolean; initialSetupData?: InstrumentSetupResponse | null }
  ) => void | Promise<void>;
};

function payloadRequiresSetup(summary: InstrumentSetupSummary | null | undefined): boolean {
  if (!summary) return false;
  return (
    summary.requires_setup === true || summary.requires_instrument_confirmation === true
  );
}

/**
 * After a successful broker sync/import, open mandatory instrument setup only when
 * something still needs confirmation. Fully configured accounts stay quiet.
 *
 * Does not POST /scan here — `openSetup` scans when the modal actually opens.
 */
export async function handleInstrumentSetupAfterSync(
  input: InstrumentSetupAfterSyncInput
): Promise<void> {
  const code = input.brokerAccountCode.trim();
  if (!code) return;

  if (!input.skipTradeAccountsRefetch) {
    await input.refetchTradeAccounts();
  }

  const fromSummary =
    payloadRequiresSetup(input.instrumentSetup) ||
    input.instrumentSetupPendingOnAccount === true;

  let shouldOpen = fromSummary;
  if (!shouldOpen) {
    try {
      const setupData = await fetchInstrumentSetup(code);
      shouldOpen = accountNeedsInstrumentSetup(setupData);
    } catch {
      return;
    }
  }

  if (!shouldOpen) return;

  await input.openMandatorySetup(code, {
    expectedPending: true,
  });
}

export function readInstrumentSetupFromPayload(
  payload: unknown
): InstrumentSetupSummary | null {
  if (!payload || typeof payload !== "object") return null;
  const root = payload as Record<string, unknown>;
  const phase = String(root.phase ?? "").trim().toLowerCase();
  if (phase === "needs_instrument_setup") {
    return {
      pending_count: 1,
      resolved_count: 0,
      total_symbols: Number(root.closed_trades ?? 0) + Number(root.open_trades ?? 0) || 0,
      requires_setup: true,
      requires_instrument_confirmation: true,
      setup_url:
        typeof root.instrument_setup_url === "string" ? root.instrument_setup_url : undefined,
    };
  }
  const raw = root.instrument_setup ?? root.instrumentSetup;
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const requiresInstrumentConfirmation = o.requires_instrument_confirmation === true;
  const requiresSetup = o.requires_setup === true || requiresInstrumentConfirmation;
  if (!requiresSetup && Number(o.pending_count ?? 0) <= 0) {
    return null;
  }
  return {
    pending_count: Number(o.pending_count ?? 0),
    resolved_count: Number(o.resolved_count ?? 0),
    total_symbols: Number(o.total_symbols ?? 0),
    requires_setup: requiresSetup,
    requires_instrument_confirmation: requiresInstrumentConfirmation || undefined,
    scan_status: typeof o.scan_status === "string" ? o.scan_status : undefined,
    setup_url:
      typeof o.setup_url === "string"
        ? o.setup_url
        : typeof root.instrument_setup_url === "string"
          ? root.instrument_setup_url
          : undefined,
  };
}

export function resolveBrokerAccountCodeFromSync(
  explicitCode: string | null | undefined,
  activeAccountCode: string | null | undefined,
  payload?: unknown
): string {
  const explicit = (explicitCode || "").trim();
  if (explicit) return explicit;

  if (payload && typeof payload === "object") {
    const p = payload as Record<string, unknown>;
    const fromPayload = String(
      p.account_code ?? p.accountCode ?? p.broker_account_code ?? p.brokerAccountCode ?? ""
    ).trim();
    if (fromPayload) return fromPayload;
  }

  return (activeAccountCode || "").trim();
}
